import { boolean, date, foreignKey, index, jsonb, pgEnum, pgTable, primaryKey, text, unique, uniqueIndex, uuid, } from 'drizzle-orm/pg-core';
import { isoTimestamp, tableBase } from '../../utils';
import { orgId, orgPrimaryKey, orgTableBase } from '../org-id';
import { Team } from '../roster/team';
import { TrackAthlete } from '../annual-planner';
import { relations, sql } from 'drizzle-orm';
import { Org } from '../org';
import { AmsClassicUser } from '../integrations/ams_classic';
import { PermissionScope, ProfileRoleType } from '../../enums';
export const User = pgTable('user', {
    ...tableBase,
    isActive: boolean('is_active').notNull(),
    isAdmin: boolean('is_admin').notNull(),
    oktaUserId: text('okta_user_id').notNull(),
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.oktaUserId),
}));
export const User_Relations = relations(User, ({ many }) => ({
    profiles: many(Profile),
}));
export const Profile = pgTable('profile', {
    ...tableBase,
    orgId,
    userId: uuid('user_id').references(() => User.id),
    isActive: boolean('is_active').notNull(),
    hasLoginAccess: boolean('has_login_access').notNull(),
    axleProfileId: text('axle_profile_id').notNull(),
    givenName: text('given_name').notNull(),
    preferredName: text('preferred_name').notNull().default(''),
    familyName: text('family_name').notNull(),
    email: text('email').notNull(),
    primaryPhone: text('primary_phone'),
    imageUrl: text('image_url'),
    gender: text('gender'),
    birthdate: date('birthdate'),
    isCoach: boolean('is_coach').notNull().default(false),
    // There are more properties from Axle, could import all if we want
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.axleProfileId),
}));
export const Profile_Relations = relations(Profile, ({ many, one }) => ({
    teamMemberships: many(ProfileTeamMembership),
    coachTeamMemberships: many(CoachTeamMembership),
    // SC-5674: the roster-facing halves, walked by the Zero roster page query
    // (athlete eligibility, sub-track parent filter). Same table, same
    // relations() export — a second export for Profile shadows this one in
    // drizzle's relational-query types.
    athleteMemberships: many(ProfileTeamMembership),
    trackAthletes: many(TrackAthlete),
    user: one(User, {
        fields: [Profile.userId],
        references: [User.id],
    }),
    org: one(Org, {
        fields: [Profile.orgId],
        references: [Org.id],
    }),
    amsClassicUser: one(AmsClassicUser, {
        fields: [Profile.orgId, Profile.id],
        references: [AmsClassicUser.orgId, AmsClassicUser.profileId],
    }),
}));
export const TeamMembershipType = pgEnum('team_member_type', [
    'coach',
    'athlete',
]);
export const ProfileTeamMembership = pgTable('profile_team_membership', {
    ...tableBase,
    orgId,
    profileId: uuid('profile_id').notNull(),
    teamId: uuid('team_id').notNull(),
    type: TeamMembershipType('type').notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    // Allows a profile to be a member of a team in two different ways - aka a coach and an athlete
    // If not desired, then remove table.type from the unique constraint
    uq1: unique().on(table.orgId, table.profileId, table.teamId, table.type),
}));
export const ProfileTeamMembership_Relations = relations(ProfileTeamMembership, ({ one }) => ({
    profile: one(Profile, {
        fields: [ProfileTeamMembership.orgId, ProfileTeamMembership.profileId],
        references: [Profile.orgId, Profile.id],
    }),
    teamMembershipAttributes: one(AthleteTeamAttributes, {
        fields: [
            ProfileTeamMembership.orgId,
            ProfileTeamMembership.profileId,
            ProfileTeamMembership.teamId,
        ],
        references: [
            AthleteTeamAttributes.orgId,
            AthleteTeamAttributes.profileId,
            AthleteTeamAttributes.teamId,
        ],
    }),
}));
export const AthleteTeamAttributes = pgTable('athlete_team_attributes', {
    ...tableBase,
    orgId,
    profileId: uuid('profile_id').notNull(),
    teamId: uuid('team_id').notNull(),
    positions: text('positions').array(),
    sportName: text('sport_name'),
    athleteYear: text('athlete_year'),
    jerseyNumber: text('jersey_number'),
    athleteStatus: text('athlete_status'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    uq: unique().on(table.orgId, table.profileId, table.teamId),
}));
export const AthleteTeamMembershipAttributes_Relations = relations(AthleteTeamAttributes, ({ many }) => ({
    // drizzle derives the join from the opposite one() side
    // (ProfileTeamMembership_Relations.teamMembershipAttributes: orgId +
    // profileId + teamId — matching the attributes row's own key). It is a
    // many: a profile can hold both a coach and an athlete membership on
    // one team, so callers wanting athlete-only rows filter type
    // themselves.
    profileTeamMemberships: many(ProfileTeamMembership),
}));
export const ProfileRole = pgTable('profile_role', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    role: ProfileRoleType('role').notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    orgFk: foreignKey({
        columns: [table.orgId],
        foreignColumns: [Org.id],
    }),
    // Each profile can only have one role per org
    uq: unique().on(table.orgId, table.profileId),
}));
export const ProfileRole_Relations = relations(ProfileRole, ({ one }) => ({
    profile: one(Profile, {
        fields: [ProfileRole.orgId, ProfileRole.profileId],
        references: [Profile.orgId, Profile.id],
    }),
    org: one(Org, {
        fields: [ProfileRole.orgId],
        references: [Org.id],
    }),
}));
/**
 * The teams a coach has access to. Rows are written by the Axle sync from
 * non-Athlete Hub user types, regardless of the `tumAccessRules` flag — the
 * flag only decides how `Profile.isCoach` is derived:
 *
 * - flag on: `isCoach` means "has at least one row here", so a row is itself
 *   the signal that the profile is a Strength coach
 * - flag off: `isCoach` comes from the admin-selected coach list and owns web
 *   access, while these rows keep syncing in the background
 *
 * So neither column alone identifies every coach across both states.
 */
export const CoachTeamMembership = pgTable('coach_team_membership', {
    ...tableBase,
    orgId,
    profileId: uuid('profile_id').notNull(),
    // This is NULLABLE to allow for the case of "All Teams", NULL would mean
    // we include ALL teams in any filters we do
    teamId: uuid('team_id'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    uq1: unique().on(table.orgId, table.profileId, table.teamId),
    // This disallows duplicate null team_ids per org + profile
    nullTeamUq: uniqueIndex()
        .on(table.orgId, table.profileId)
        .where(sql `${table.teamId} IS NULL`),
}));
export const CoachTeamMembership_Relations = relations(CoachTeamMembership, ({ one }) => ({
    profile: one(Profile, {
        fields: [CoachTeamMembership.orgId, CoachTeamMembership.profileId],
        references: [Profile.orgId, Profile.id],
    }),
}));
export const ProfilePermission = pgTable('profile_permission', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    scope: PermissionScope('scope').notNull().default('org'),
    permissions: jsonb('permissions')
        .$type()
        .notNull()
        .default({}),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    orgFk: foreignKey({
        columns: [table.orgId],
        foreignColumns: [Org.id],
    }),
    uq: unique().on(table.orgId, table.profileId),
}));
export const ProfilePermission_Relations = relations(ProfilePermission, ({ one }) => ({
    profile: one(Profile, {
        fields: [ProfilePermission.orgId, ProfilePermission.profileId],
        references: [Profile.orgId, Profile.id],
    }),
    org: one(Org, {
        fields: [ProfilePermission.orgId],
        references: [Org.id],
    }),
}));
export const ProfilePermissionAudit = pgTable('profile_permission_audit', {
    id: uuid('id').notNull(),
    orgId,
    createdAt: isoTimestamp('created_at').default(sql `now()`),
    createdById: uuid('created_by_id'),
    targetProfileId: uuid('target_profile_id').notNull(),
    previousPermissions: jsonb('previous_permissions').$type(),
    newPermissions: jsonb('new_permissions')
        .$type()
        .notNull(),
    previousScope: PermissionScope('previous_scope'),
    newScope: PermissionScope('new_scope').notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    targetIdx: index('profile_permission_audit_target_idx').on(table.orgId, table.targetProfileId, table.createdAt),
}));
//# sourceMappingURL=index.js.map